/* eslint-disable @typescript-eslint/ban-types */
import * as React from "react";
import { shallowEqual, useSelector } from "react-redux";
import { HeaderGroup, Row, useTable } from "react-table";
import classNames from "classnames";
import { Types, Components, Utils, Logger, Services } from "@sendum/core";

import { VisibilityRootState } from "../store/VisibilityStore";

import classes from "./VisibilityHistoryTable.module.scss";
import { IAnyObject } from "@sendum/core/dist/interfaces";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityHistoryTable");

interface IProps {
	virtualShipmentName?: string;
}

export const VisibilityHistoryTable = ({ virtualShipmentName }: IProps): JSX.Element => {
	logger.debug("render");
	const device = useSelector((state: VisibilityRootState) => state.Visibility.device, shallowEqual);
	const history = useSelector((state: VisibilityRootState) => state.Visibility.history);
	const historyLoading = useSelector((state: VisibilityRootState) => state.Visibility.historyLoading);

	const sensors = React.useMemo(() => {
		if (!device) {
			return [];
		}

		return [...(device.getHistoryTableConfig().additionalSensors ?? []), ...Array.from(device.getAttributesConfig().keys())];
	}, [device]);

	const isLoading: boolean = React.useMemo(() => {
		if (!device) {
			return false;
		}

		return sensors.some(sensor => historyLoading.get(`${device.id}#${device.shipment.id}#${sensor}`));
	}, [historyLoading, device, sensors]);

	const columns = React.useMemo<Types.TTableColumn[]>(() => device?.getHistoryTableConfig().columns ?? [], [device]);

	const data = React.useMemo<IAnyObject[]>(() => {
		if (!device) {
			return [];
		}

		if (isLoading) {
			return [];
		}

		const dataMap = new Map<number, IAnyObject>();
		const latKey = "lat";
		const lngKey = "lng";
		sensors.forEach(sensor => {
			const deviceHistory = history.get(`${device.id}#${virtualShipmentName || device.shipment.id}#${sensor}`);
			deviceHistory?.forEach((item: Types.TProperty) => {
				if (item && item.epoch) {
					const dataItem = dataMap.get(item.epoch) || {};
					const newItem: IAnyObject = {};
					if (sensor === "location") {
						newItem[latKey] = (item as Types.TLocationProperty)?.latitude;
						newItem[lngKey] = (item as Types.TLocationProperty)?.longitude;
					} else {
						const sensorValue = (item as (Types.TBooleanProperty | Types.TNumberProperty))?.value;
						const sensorConfig = device?.getAttributesConfig().get(sensor);
						newItem[sensor] = Utils.getSensorDisplayValue(sensorValue, sensorConfig);
					}
					dataMap.set(item.epoch, { ...dataItem, ...newItem });
				}
			});
		});

		return Array.from(dataMap.keys()).sort((a, b) => a - b)
			.map(epoch => ({ epoch: Utils.time.format(epoch, "YYYY-MMM-D HH:mm"), ...dataMap.get(epoch), id: epoch }));
	}, [history, isLoading, sensors, device, virtualShipmentName]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
	} = useTable(
		{
			columns,
			data,
		},
	);

	const scrollToKeyInTable = React.useCallback((_newKey: number): void => {
		const newKey = (_newKey + "").replace(/0*$/g,"");
		if (newKey) {
			const node: HTMLElement = document.querySelectorAll(`[id^="${newKey}"]`)[0] as HTMLElement;//
			const location = rows.filter(row => row.original.lat !== undefined && row.original.lng !== undefined && (row.original.id + "").startsWith(newKey))[0];
			if (location) {
				Services.EventEmitter.emit("data_table_map", { lat: location.original.lat, lng: location.original.lng });
			}
			try {
				const row = node;
				const tableBodyNode = row?.parentElement;
				if (tableBodyNode && row?.offsetTop) {
					tableBodyNode.scrollTop = row?.offsetTop - row.offsetHeight;
				}
				const { scrollX, scrollY } = window;
				node?.focus({ preventScroll: true });
				window.scrollTo(scrollX, scrollY);
			} catch (e) {}
		}
	},[rows]);

	React.useEffect(() => {
		Services.EventEmitter.on("data_table", scrollToKeyInTable);

		return () => {
			Services.EventEmitter.off("data_table", scrollToKeyInTable);
		};
	}, [scrollToKeyInTable]);

	const renderHeaderCell = (column: HeaderGroup<IAnyObject>): JSX.Element => (
		<th
			key={column.getHeaderProps().key}
			className={classNames(classes.historyTable__cell, classes.historyTable__headerCell)} >{column.render("Header")}</th>
	);

	const renderRow = (row: Row<IAnyObject>): JSX.Element => {
		prepareRow(row);

		return (
			<tr key={row.getRowProps().key} id={row.original.id + ""}>
				{row.cells.map(cell => (
					<td
						key={cell.getCellProps().key}
						className={classes.historyTable__cell}>
						{cell.value !== undefined ? cell.render("Cell") : "--"}
					</td>
				))}
			</tr>
		);
	};

	return (
		<div className={classes.historyTable}>
			<Components.LoadingComponent loading={isLoading} className={classes.loading} />
			<table {...getTableProps()} data-testid="shipment-data-table">
				<thead>
					{headerGroups.map(headerGroup => (
						<tr key={headerGroup.getHeaderGroupProps().key}>
							{headerGroup.headers.map(renderHeaderCell)}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{rows.map(renderRow)}
				</tbody>
			</table>
		</div>
	);
};
